/* eslint-disable max-lines */
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, lastValueFrom, of } from 'rxjs';
import {
  catchError, map, take, tap, timeout,
} from 'rxjs/operators';

import { ClientFilterObject, ComparisonWhereFilter, WhereFilter } from '@common/filter.decorator';

import { C } from 'src/app/@shared/constants';
import { ContentOptions, ContentService } from 'src/app/@core/content.service';
import { ProtoUrl } from 'src/app/@shared/protoUrl';

import { FileUploadService } from 'src/app/@core/file-upload.service';
import { FormMainType } from '@lib/types';
import { LatestDataReviewSummary, OrganizationModel, OrganizationSource } from 'src/app/organization/@shared/organization.model';
import { OrganizationSubType } from '@api/organization/@shared/organization.entity';
import { File as ProtoFile, Protocol } from '@common/entities';
import { ProtocolService } from 'src/app/protocol/@shared/protocol.service';
import { environment } from 'src/environments/environment';

const ORGANIZATION_CSV_FIELD_RELATIONS: { [fieldKey: string]: string } = {
  branchOffices: 'branchOffices',
  contactOrganization: 'contactOrganization',
  localContactPoint: 'localContactPoint',
  mainOffice: 'mainOffice',
  providedBy: 'providedBy',
  provider: 'provider',
  providesFor: 'providesFor',
};

export const NAKOS_ORGANIZATION_DETAIL_STORE_ID = 'nakosDetail';

const LEGACY_ID_LOOKUP_TAKE = 2;
const PUBLIC_TOPIC_ADDRESS_PAGE_SIZE = 50;
const PUBLIC_DETAIL_STORE_PREFIX = 'public-detail:';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService extends ContentService<OrganizationModel, OrganizationSource> {
  private path = 'organizations';

  constructor(private http: HttpClient,
    private fileUploadService: FileUploadService,
    private protocolService: ProtocolService) {
    super();
  }

  public loadContent(id: string, options: ContentOptions<OrganizationSource> = {}): Observable<OrganizationModel[] | null> {
    if (!options.type) {
      console.warn('[OrganizationService] loadContent(id, options) no type provided -> defaults to loadListContent');
    }

    if (options.type === 'publicDetail') {
      return this.loadPublicDetailContent(id.slice(PUBLIC_DETAIL_STORE_PREFIX.length)).pipe(map((item) => {
        return item
          ? [item]
          : null;
      }));
    }

    if (options.type !== 'detail') {
      return this.loadListContent(id, options);
    }

    if (!options.withoutAuthentication) {
      options.filter ||= {};
      options.filter.relations ||= [];
      options.filter.relations.push('providesFor');
      options.filter.relations.push('providedBy');
      options.filter.relations.push('mainOffice');
      options.filter.relations.push('branchOffices');
      options.filter.relations.push('localContactPoint');
      options.filter.relations.push('contactOrganization');
      options.filter.relations.push('provider');
    }

    return this.loadDetailContent(id, options).pipe(map((item: OrganizationModel | null) => {
      return item
        ? [item]
        : null;
    }));
  }

  public exportCsv(organizationId: string, selectedFieldKeys: string[] = []): Observable<Blob> {
    return this.exportCsvWithOptions(organizationId, selectedFieldKeys, false);
  }

  public exportCsvWithOptions(organizationId: string, selectedFieldKeys: string[] = [], withoutAuthentication: boolean = false): Observable<Blob> {
    let headers = new HttpHeaders({
      Accept: 'text/csv',
    });

    if (withoutAuthentication) {
      headers = headers.set('x-skip-auth', 'true');
    }

    const selectedRelations = selectedFieldKeys.reduce<string[]>((relations, fieldKey) => {
      const relation = ORGANIZATION_CSV_FIELD_RELATIONS[fieldKey];

      if (relation && !relations.includes(relation)) {
        relations.push(relation);
      }

      return relations;
    }, []);

    const filter: ClientFilterObject<OrganizationSource> | null = selectedRelations.length
      ? {
        relations: selectedRelations,
      }
      : null;

    const queryParams = selectedFieldKeys.length
      ? {
        fields: selectedFieldKeys.join(','),
      }
      : null;

    const url = ProtoUrl.getUrl(`${this.path}/${organizationId}`, {
      filter,
      queryParams,
    });

    return this.http.get(url, {
      headers,
      responseType: 'blob',
    });
  }

  public create(attributes: Partial<OrganizationSource>, mainStoreId: FormMainType, subStoreId?: OrganizationSubType): Promise<OrganizationModel> {
    return this.http.post<OrganizationSource>(ProtoUrl.getUrl(this.path), attributes)
      .pipe(map((item: OrganizationSource) => { return new OrganizationModel(item); }),
        tap((item: OrganizationModel) => {
          this.loadProtocolsForOrganization(item.id).catch();

          if (this.metadataStore[mainStoreId]) {
            this.metadataStore[mainStoreId].totalCount += 1;
          }

          this.updateItem(item, mainStoreId, true);

          if (subStoreId) {
            this.updateItem(item, subStoreId, true);

            if (this.metadataStore[subStoreId]) {
              this.metadataStore[subStoreId].totalCount += 1;
            }
          }
        }))
      .toPromise();
  }

  public patch(id: string, attributes: Partial<OrganizationSource>, mainStoreId: FormMainType, lastKnownSubType?: OrganizationSubType):
  Promise<OrganizationModel> {
    return this.http.patch<OrganizationSource>(ProtoUrl.getUrl(`${this.path}/${id}`), attributes)
      .pipe(map((item: OrganizationSource) => { return new OrganizationModel(item); }),
        tap((item: OrganizationModel) => {
          this.loadProtocolsForOrganization(item.id).catch();
          this.invalidatePublicDetailContent(item.id);

          if (!lastKnownSubType || lastKnownSubType === item.subType) {
            this.updateItem(item);

            return;
          }

          this.updateItem(item, item.subType, true);
          this.updateItem(item, mainStoreId, true);
          this.removeItem(id, lastKnownSubType);
        }))
      .toPromise();
  }

  public delete(id: string): Promise<boolean> {
    return this.http.delete<OrganizationSource>(ProtoUrl.getUrl(`${this.path}/${id}`))
      .pipe(map(() => {
        return true;
      }),
      tap(() => {
        this.invalidatePublicDetailContent(id);
        this.removeItem(id);
      }))
      .toPromise();
  }

  public updateLatestDataReviewSummary(organizationId: string, latestDataReview: LatestDataReviewSummary | null): void {
    if (!organizationId) {
      return;
    }

    this.invalidatePublicDetailContent(organizationId);
    this.updateItem({
      id: organizationId,
      latestDataReview: latestDataReview || null,
    } as OrganizationModel);
  }

  public async resolveReviewedFile(originalFile: ProtoFile | null,
    submittedFile: ProtoFile | null, // The file from the data review
    finalFile: File | null): Promise<string | null> {
    let finalFileId: string | null = null;

    if (finalFile instanceof File) {
      // the reviewer uploaded a new file, so we need to upload it
      try {
        const { id } = await this.fileUploadService.upload(finalFile, finalFile.type);

        finalFileId = id;
      } catch (error) {
        console.error('[OrganizationService] Reviewer file upload failed', error);
        throw error;
      }
    } else if (finalFile) {
    // the reviewer kept an existing file, so its ID is the final ID
      finalFileId = (finalFile as ProtoFile).id;
    }

    // mark replaced files for deletion
    const idsToDelete = new Set<string>();

    if (originalFile && originalFile.id !== finalFileId) {
      idsToDelete.add(originalFile.id);
    }
    if (submittedFile && submittedFile.id !== finalFileId) {
      idsToDelete.add(submittedFile.id);
    }

    // delete obsolete files
    if (idsToDelete.size > 0) {
      idsToDelete.forEach((id) => {
        this.fileUploadService.delete(id).catch((err) => {
          console.error(`[OrganizationService] Failed to delete obsolete file ${id}`, err);
        });
      });
    }

    return finalFileId;
  }

  // only upload files, so they can be properly displayed in the to-be-reviewed form
  // dont delete files (this will happen as soon the review is done)
  public async handleReviewRequestFile(oldFile?: ProtoFile, newFile?: ProtoFile | File): Promise<string | null> {
    const action = this.fileUploadService.getFileAction(oldFile, newFile);
    let newFileId: string | null = null;

    if (action === 'create' || action === 'update') {
      const fileToUpload = newFile as File;

      try {
        const { id } = await this.fileUploadService.upload(fileToUpload, fileToUpload.type);

        newFileId = id;
      } catch (error) {
        console.error('Upload failed during review request, aborting.', error);
        throw error;
      }

      return newFileId;
    }

    if (action === 'delete') {
    // dont actually delete the file, just record it by setting the fileId to null
      return newFileId;
    }

    // 'noAction'
    return oldFile?.id ?? null;
  }

  public loadPublicTopicAddresses(topicId: string, skip: number): Observable<{ items: OrganizationModel[]; totalCount: number }> {
    const listId = `topic-addresses-${topicId}`;

    return this.loadContent(listId, {
      filter: {
        order: {
          name: 'ASC',
        },
        skip,
        take: PUBLIC_TOPIC_ADDRESS_PAGE_SIZE,
        where: {
          topics: {
            id: topicId,
          },
        },
      },
      type: 'list',
      withoutAuthentication: true,
    }).pipe(map(() => {
      return {
        items: this.getStore(listId) || [],
        totalCount: this.getMetaData(listId).totalCount || 0,
      };
    }));
  }

  public getPublicDetailContent(id: string, forceReload = false): Observable<OrganizationModel | null> {
    const storeId = `${PUBLIC_DETAIL_STORE_PREFIX}${id}`;

    return this.getContent(storeId, {
      forceReload,
      type: 'publicDetail',
      withoutAuthentication: true,
    }).pipe(take(1), map((items) => {
      const { error } = this.getMetaData(storeId);

      if (error) {
        throw error;
      }

      return items?.[0] || null;
    }));
  }

  public getPublicDetailStoreItem(id: string): OrganizationModel | null {
    return this.getStore(`${PUBLIC_DETAIL_STORE_PREFIX}${id}`)?.[0] || null;
  }

  public invalidatePrivateDetailContent(id: string): void {
    this.items[id]?.next(null);
    delete this.itemsStore[id];
  }

  public invalidatePublicDetailContent(id: string): void {
    const storeId = `${PUBLIC_DETAIL_STORE_PREFIX}${id}`;

    this.items[storeId]?.next(null);
    delete this.itemsStore[storeId];
  }

  public loadPublicDetailContent(id: string): Observable<OrganizationModel | null> {
    return this.loadDetailContent(`${PUBLIC_DETAIL_STORE_PREFIX}${id}`, {
      type: 'publicDetail',
      withoutAuthentication: true,
    });
  }

  /**
   * Unauthenticated lookup of a legacy nakos.de address id (redirected old links) via the public
   * exact `legacyId` filter. Resolves to null when nothing matches or the id is ambiguous.
   */
  public findPublicByLegacyId(legacyId: string): Observable<OrganizationModel | null> {
    const url = ProtoUrl.getUrl(this.path, {
      filter: {
        // one row resolves, two rows prove ambiguity
        take: LEGACY_ID_LOOKUP_TAKE,
        where: {
          legacyId,
          type: 'selfHelpAddress',
        },
      },
    });

    return this.http.get<OrganizationSource[]>(url, this.getRequestOptions(true))
      .pipe(timeout(C.DEFAULT_TIMEOUT), map((items) => {
        const matches = items || [];

        if (matches.length > 1) {
          // legacy ids are expected to be unique; surface duplicates instead of guessing
          console.warn('[OrganizationService] ambiguous legacy id', legacyId, matches.map((item) => {
            return item.id;
          }));
        }

        return matches.length === 1
          ? new OrganizationModel(matches[0])
          : null;
      }));
  }

  public loadNakosOrganizationDetail(): Observable<OrganizationModel | null> {
    const url = ProtoUrl.getUrl(`${this.path}/${environment.nakosOrganizationId}`);

    return this.http.get<OrganizationSource>(url, this.getRequestOptions(true))
      .pipe(timeout(C.DEFAULT_TIMEOUT),
        map((item) => {
          return item
            ? new OrganizationModel(item)
            : null;
        }),
        tap((item) => {
          if (item) {
            this.setDetailStoreItem(item.id, item);
            this.setDetailStoreItem(NAKOS_ORGANIZATION_DETAIL_STORE_ID, item);
          } else {
            this.itemsStore[NAKOS_ORGANIZATION_DETAIL_STORE_ID] = [];
            this.updateItemsBehaviorSubjects([NAKOS_ORGANIZATION_DETAIL_STORE_ID]);
          }
        }),
        catchError((error) => {
          console.warn('[OrganizationService] Could not load NAKOS organization detail', error);
          this.itemsStore[NAKOS_ORGANIZATION_DETAIL_STORE_ID] = [];
          this.updateItemsBehaviorSubjects([NAKOS_ORGANIZATION_DETAIL_STORE_ID]);

          return of(null);
        }));
  }

  public getNakosOrganizationDetail(): OrganizationModel | null {
    return this.getStore(NAKOS_ORGANIZATION_DETAIL_STORE_ID)?.[0] || null;
  }

  protected loadListContent(id: string, options: ContentOptions<OrganizationSource>): Observable<OrganizationModel[]> {
    const filter: ClientFilterObject<OrganizationSource> = {
      ...this.getNormalizedFilter(id, options.filter),
      where: {
        ...options.filter.where,
      },
    };

    const where = filter.where as WhereFilter<OrganizationSource> & { multiFieldSearch?: string };
    const hasMultiFieldSearch = typeof where.multiFieldSearch === 'string' && !!where.multiFieldSearch.trim();

    // Let API apply relevance ordering for multi-field search.
    if (hasMultiFieldSearch) {
      delete filter.order;
    }

    // use comparison filter for `updatedAt`
    if (where.updatedAt) {
      where.updatedAt = {
        ge: where.updatedAt,
      } as ComparisonWhereFilter;
    }

    const url = ProtoUrl.getUrl(this.path, {
      filter,
    });

    console.info('[OrganizationService] load items', url);
    let totalCount: number;

    return this.http.get<OrganizationSource[]>(url, {
      ...this.getRequestOptions(options.withoutAuthentication),
      observe: 'response',
    })
      .pipe(
        timeout(C.DEFAULT_TIMEOUT),
        tap((response: HttpResponse<OrganizationSource[]>) => {
          const { headers } = response;

          totalCount = parseInt(headers.get('x-total-count')) || (response.body?.length || 0);
        }),
        map((response: HttpResponse<OrganizationSource[]>) => { return response.body || []; }),
        map((items: OrganizationSource[]) => { return items.map((item) => { return new OrganizationModel(item); }); }),
        tap((items: OrganizationModel[]) => {
          this.setItems(items, id, filter, totalCount);
        }),
      );
  }

  protected loadDetailContent(id: string, options: ContentOptions<OrganizationSource>): Observable<OrganizationModel> {
    const { filter } = options;
    const organizationId = options.type === 'publicDetail'
      ? id.slice(PUBLIC_DETAIL_STORE_PREFIX.length)
      : id;
    const url = ProtoUrl.getUrl(`${this.path}/${organizationId}`, {
      filter,
    });

    return this.http.get<OrganizationSource>(url, this.getRequestOptions(options.withoutAuthentication))
      .pipe(timeout(C.DEFAULT_TIMEOUT),
        map((item) => {
          return item
            ? new OrganizationModel(item)
            : null;
        }),
        tap((item) => {
          if (item) {
            this.setDetailStoreItem(id, item);
          } else {
            this.itemsStore[id] = [];
            this.updateItemsBehaviorSubjects([id]);
          }

          this.metadataStore[id] = {
            ...this.metadataStore[id],
          } as any;
        }));
  }

  private getRequestOptions(withoutAuthentication: boolean = false): { headers?: HttpHeaders } {
    if (!withoutAuthentication) {
      return {};
    }

    return {
      headers: new HttpHeaders({
        'x-skip-auth': 'true',
      }),
    };
  }

  private setDetailStoreItem(storeId: string, item: OrganizationModel): void {
    this.itemsStore[storeId] = [item];
    this.updateItemsBehaviorSubjects([storeId]);
  }

  private async loadProtocolsForOrganization(organizationId: string): Promise<void> {
    const filter: ClientFilterObject<Protocol> = {
      where: {
        editedEntityId: organizationId,
        editedEntityType: 'organization',
      },
    };

    await lastValueFrom(this.protocolService.loadContent(organizationId, {
      filter,
      type: 'list',
    }));
  }
}
